/**
 * pickDrawerStatusFamily — pure helper that maps a workflow status (+ optional
 * HITL question presence) to a coarse status family used by the drawer to
 * pick its sub-view, default expansion, and per-family localStorage key.
 *
 * Replaces the scattered ``effectiveStatus === '...'`` chains in
 * WorkflowDetailDrawer with a single switch. The full set of sub-views
 * (ActiveDrawerBody, HitlDrawerBody, CompletedDrawerBody,
 * TerminalErrorDrawerBody, TerminalCancelledDrawerBody) lands in PR-6.
 *
 * Unknown statuses fall through to ``terminal_error`` with a dev-only
 * console warning (de-duped via a module-level Set so we don't spam).
 * Protects against future server-added statuses landing without UI changes.
 */

export type DrawerStatusFamily =
  | "active"
  | "hitl"
  | "pending_email_send"
  | "pending_edocs"
  | "completed"
  | "terminal_error"
  | "terminal_cancelled";

const warnedStatuses = new Set<string>();

/**
 * Map a workflow status (string) + optional HITL-question presence to a
 * drawer status family. The hitlQuestion parameter is loose-typed so the
 * helper can stay free of the drawer's concrete HitlQuestion type.
 */
export function pickDrawerStatusFamily(
  status: string | null | undefined,
  hitlQuestion?: unknown,
): DrawerStatusFamily {
  // pending_edocs is checked BEFORE the hitlQuestion short-circuit below: the
  // eDoc cold gate always carries a (reject-only) hitl_question to render the
  // "Check Now" / Reject side actions, so the short-circuit would otherwise
  // force it into the "hitl" (Input Needed) family. It needs no AM decision, so
  // it gets its own calm family (mirrors pending_email_send).
  if (status === "pending_edocs") return "pending_edocs";

  // HITL takes precedence — a paused/warm HITL with a live question goes
  // into the HITL family regardless of the underlying status enum.
  if (hitlQuestion) return "hitl";

  // null/undefined is the "status not loaded yet" sentinel that callers
  // (e.g. WorkflowDetailDrawer.effectiveStatus) deliberately pass while the
  // push/poll status is still in flight. It is NOT an unknown server status:
  // treat it as the neutral "active" family — never warn, and never flash the
  // drawer into the terminal_error view for an instance that is merely loading.
  // Only a genuinely-unknown *non-null* status string hits the warn + fallback
  // branch below (the safety net for server-added statuses landing without UI).
  if (status == null) return "active";

  switch (status) {
    case "running":
      return "active";
    case "pending_email_send":
      // Parked waiting for the AM to send the reply — its own family renders a
      // "hand-off" body (done in Epic, go send the draft), NOT the active
      // step pipeline which would look like it's still running.
      return "pending_email_send";
    case "paused_hitl":
    case "warm_hitl":
    case "rpa_review":
      return "hitl";
    // NB: pending_edocs is handled above (before the hitlQuestion
    // short-circuit) — its own calm family, not "hitl".
    case "completed":
      return "completed";
    case "failed":
    case "rejected":
    case "timeout_cancelled":
      return "terminal_error";
    case "cancelled":
      return "terminal_cancelled";
    default: {
      const key = String(status ?? "<null>");
      if (
        typeof process !== "undefined" &&
        process.env?.NODE_ENV !== "production" &&
        !warnedStatuses.has(key)
      ) {
        warnedStatuses.add(key);
        // eslint-disable-next-line no-console
        console.warn(
          `[pickDrawerStatusFamily] Unknown workflow status "${key}" — falling back to 'terminal_error'.`,
        );
      }
      return "terminal_error";
    }
  }
}
